import {inject, Injectable} from '@angular/core';
import {
  BaseReq,
  CancelPlanResponse,
  Copy,
  ExpiredPlanPayload,
  IBaseResponse,
  MyPlansResponse,
  Offers,
  OrderRequest,
  OrderResponse,
  PaymentHistory,
  PaymentHistoryPaymentMethod,
  PaymentHistoryPaymentMethods,
  PaymentMethodsAPIM,
  PlanBillingType,
  PlanSummary,
  PricingDataItem,
  RecurringCharges,
  ReviewData,
  ServiceUsageType,
  StatusResponse,
  UpdatePaymentMethodRequest
} from '@ox/typings';
import {ApiDataService, Header} from '@ox/data';
import {ApiCacheService, BML, getBaseHeaders, replaceVariable} from '@ox/utils';
import {BehaviorSubject, catchError, map, Observable, skipWhile, switchMap, take} from 'rxjs';
import {apiUrlMap, apiUrls} from '@ox/config';
import {ShopService} from '../shop-service';

/** Data service for calling all shop-related BFF API calls */
@Injectable()
export class ShopDataService {
  private rebuttalData$ = inject(ShopService).rebuttalData$;
  private _copy = new BehaviorSubject<Copy>({} as Copy);
  private bml = inject(BML);

  public brand = this.bml.brand;
  public market = this.bml.market;

  get copy() {
    return this._copy.getValue();
  }

  set copy(copy: Copy) {
    this._copy.next(copy);
  }

  baseBffUrl = '/oc-shop-service/bff/';

  constructor(
    private api: ApiDataService,
    private apiCache: ApiCacheService
  ) {}

  getBaseHeaders = () => getBaseHeaders(this.bml);

  getPricingData = (planId: string, vin: string) => {
    const headers = this.getBaseHeaders().concat([
      {planId},
      {vin}
    ]);
    return this.apiCache
      .get<IBaseResponse<PricingDataItem>>({tokenOrUrl: apiUrls.authPricing.TOKEN, force: true, headers});
  };

  getUnauthPricingData = (planId: string) => {
    const headers = this.getBaseHeaders().concat([{planId: planId}]);
    return this.apiCache.get<IBaseResponse<PricingDataItem>>({tokenOrUrl: apiUrls.unAuthPricing.TOKEN, force: true, headers});
  };

  getOffers(vin: string) {
    const headers = this.getBaseHeaders().concat({vin});
    return this.apiCache.get<Offers>({tokenOrUrl: apiUrls.authOffers.TOKEN, force: true, headers});
  }

  getUnauthOffers() {
    return this.apiCache.get<Offers>({tokenOrUrl: apiUrls.unAuthOffers.TOKEN, headers: this.getBaseHeaders()});
  }

  getCopyData = () => {
    return this.apiCache.get<Copy[]>({
      tokenOrUrl: apiUrls.copy.TOKEN,
      urlParams: [this.bml.brand.toUpperCase(), this.bml.market.toUpperCase()],
      headers: this.getBaseHeaders(),
      localized: true
    })
    .pipe(
      take(1),
      skipWhile((x) => !x),
      map((response) => {
        if (response?.data) {
          this.copy = response.data as Copy;
          return this.copy;
        } else {
          throw new Error('No Copy');
        }

      }),
      catchError(() => {
        throw new Error('Copy Retrieval failed');
      })
    );
  };

  public getComparePlanData(authState: boolean, copyApiHeaders:{[key:string]:string}) {
    let headers = this.getBaseHeaders();
    if((copyApiHeaders['vin'])) headers = headers.concat(copyApiHeaders);
    const authComparePlanUrl = apiUrls.authComparePlan.TOKEN;
    const unAuthComparePlanUrl = apiUrls.unauthComparePlan.TOKEN;
    return this.apiCache.get({tokenOrUrl: authState ? authComparePlanUrl : unAuthComparePlanUrl, headers})
      .pipe(
        map((response) => {
          if (response && response.data) return response.data;
          else throw new Error('error fetching Compare plan data');
        }),
        catchError((error) => {throw new Error(error);})
      );
  }

  getGenericCopy(id: string, variableMap: Record<string, string> = {}): string | undefined {
    const str = this.copy?.generic?.find((copy: any) => copy.copyDocKey === id)?.copyDocValue;
    return str ? replaceVariable(str, variableMap) : str;
  }

  getProductCopyByCode(name: string, variableMap: Record<string, string> = {}) {
    const copyProduct = this.copy.products?.find(
      (product) => product.code?.toLocaleLowerCase() === name.toLocaleLowerCase()
    );
    if (copyProduct === undefined) {
      return undefined;
    }
    copyProduct.description = replaceVariable(
      copyProduct?.description ?? '',
      variableMap
    );
    return copyProduct;
  }
  getProductCopyByName(name: string, variableMap: Record<string, string> = {}) {
    let copyProduct = this.copy.products?.find(
      (product) => product.name?.toLocaleLowerCase() === name.toLocaleLowerCase()
    );

    if(copyProduct === undefined) {
      copyProduct = this.copy.products?.find(
        (product) => product.displayName?.toLocaleLowerCase() === name.toLocaleLowerCase()
      );
    }

    if (copyProduct === undefined) {
      return undefined;
    }
    copyProduct.description = replaceVariable(
      copyProduct?.description ?? '',
      variableMap
    );
    return copyProduct;
  }

  getAllCopyProducts(){
    return this.copy.products;
  }
  getPlanCodeBySeo(seoUrl:string){
    return this.copy.products?.find((product) => product.seoUrl?.toLowerCase() === seoUrl.toLowerCase())?.code;
  }
  getAllAlerts(){
    return this.copy.alerts;
  }
  getAlertsByAlertIds (alertsIds:string[]){
    return this.copy.alerts?.filter((alert) => alertsIds.includes(alert.name));
  }

  getReviewData = (accountId: string, offerId: string, channel: string) => {
    const headers = this.getBaseHeaders().concat([
      {Accountnumber: accountId},
      {OfferIds: offerId},
      {Channel: channel}
    ]);
    return this.api.get<ReviewData>(
      '/onStar/shop/review',
      undefined,
      undefined,
      undefined,
      undefined,
      headers
    );
  };

  getPaymentMethods = (accountId: string) => {
    const headers = this.getBaseHeaders().concat([
      {accountId: accountId}
    ]);
    return this.api.get<PaymentMethodsAPIM>(
      '/oc-passthrough-service/paymentMethods',
      undefined,
      undefined,
      undefined,
      undefined,
      headers
    );
  };

  getOcPaymentMethods = (accountNumber: string) => {
    const headers = this.getBaseHeaders().concat([
      {accountNumber: accountNumber}
    ]);

    return this.apiCache.get<PaymentMethodsAPIM>({
      tokenOrUrl: '/oc-payments/paymentMethods',
      headers
    })
      .pipe(
        take(1),
        map((data) => {
          if (data) return data;
          throw new Error('No Payment Methods');
        }),
        catchError((error) => {
          throw new Error(error);
        })
      );
  };

  updatePaymentMethod = (accountId: string, request: BaseReq, paymentMethodId: string) => {
    const headers = this.getBaseHeaders().concat([
      {accountId: accountId},
      {paymentMethodId: paymentMethodId}
    ]);
    return this.api.post(
      '/oc-passthrough-service/paymentMethods',
      undefined,
      undefined,
      request,
      undefined,
      headers
    );
  };

  switchPaymentMethod = (accountId: string, paymentMethodId: string) => {
    const headers = this.getBaseHeaders().concat([
      {accountId: accountId},
      {paymentMethodId: paymentMethodId}
    ]);
    return this.api.post(
      '/oc-passthrough-service/paymentMethods/default',
      undefined,
      undefined,
      undefined,
      undefined,
      headers
    );
  };

  deletePayment = (accountId: string, paymentMethodId: string) => {
    const headers = this.getBaseHeaders().concat([
      {accountId: accountId},
      {paymentMethodId: paymentMethodId}
    ]);
    return this.api.delete(
      '/oc-passthrough-service/paymentMethods',
      undefined,
      headers,
      undefined
    );
  };

  placeOrder = (request: OrderRequest) => {
    return this.apiCache.post<OrderResponse>({
      tokenOrUrl: apiUrls.orderApiEndPoint.URL,
      headers: this.getBaseHeaders(),
      data: request
    });
  };

  checkStatus = (reqId: string, accountNumber: string) => {
    const headers = this.getBaseHeaders().concat([
      {requestId: reqId},
      {accountNumber: accountNumber}
    ]);
    return this.apiCache.get<any>({
      tokenOrUrl: apiUrls.statusApiEndPoint.URL,
      force: true,
      headers: headers
    });
  };

  checkCancelStatus = (vin: string) => {
    const headers = this.getBaseHeaders().concat([
      {vin: vin}
    ]);
    return this.apiCache.get<StatusResponse>({
      tokenOrUrl: `${this.baseBffUrl}order/status/v2`,
      headers
    }).pipe(
      take(1),
      map(({data}) => {
        if (data) return data;
        throw new Error('Check Cancel Plan Status Failed');
      }),
      catchError((error) => {throw new Error(error);})
    );
  };

  getSOPTOT = (headersReq: any) => {
    return this.api.get<any>(
      '/onStar/transaction',
      undefined,
      headersReq,
      undefined,
      undefined,
      undefined
    );
  };

  getOrderTimeout = () => {
    return this.apiCache.get({
      tokenOrUrl: `${this.baseBffUrl}order/timeout/v2`
    });
  };

  getPCIAddPaymentUrl = (urlKey: string) => {
    const allowablePaymentTypes = 'MTHD_ACH,MTHD_CRED,MTHD_PAYPAL';
    const requestURL = '/pci/redirectpci/?allowedpt=' + allowablePaymentTypes + `&urlkey=${urlKey}`;
    return this.api.get<void>(
      requestURL,
      undefined,
      undefined,
      undefined,
      undefined,
      this.getBaseHeaders()
    );
  };

  getMyPlansData = (vin:string,globalProfileId:string,state?:string):Observable<MyPlansResponse> => {
    let headers = this.getBaseHeaders().concat([
      {vin},
      {globalProfileId}
    ]);

    if (state) headers = headers.concat({state: state.toUpperCase()});

    return this.apiCache.get<MyPlansResponse>({
      tokenOrUrl: apiUrlMap.myPlans.getMyPlans,
      headers
    }).pipe(
      take(1),
      map(({data}) => {
        if(data) return data;
        else throw new Error('No My Plans Data');
      }),
      catchError((error) => {
        throw new Error(error);
      })
    );
  };

  getExpiryStatusPresented = (vin: string, onStarAcctId: string, plansAndOrdersIds: ExpiredPlanPayload[] ) => {
    const headers = this.getBaseHeaders().concat([
      {vin: vin}
    ]);
    return this.api.post(
      '/onStar/shop/expiryStatusPresented',
      undefined,
      undefined,
      {
        accountNumber: onStarAcctId,
        vin: vin,
        plansAndOrdersIds: plansAndOrdersIds
      },
      undefined,
      headers
    ).pipe(
      take(1),
      map(({data}) => {
        return data;
      }),
      catchError((error) => {
        throw new Error(error);
      })
    );
  };

  getServiceUsage(vin : string, externalOrderNumber: string): Observable<ServiceUsageType>{
    const headers = this.getBaseHeaders().concat([
      {vin: vin},
      {externalOrderNumber: externalOrderNumber}
    ]);

    return this.api.get <ServiceUsageType>(apiUrlMap.myPlansUnified.serviceUsage,
      undefined,
      undefined,
      undefined,
      undefined,
      headers
      ).pipe(
      map((response) => {
        if (response) {
          return response.data;
        }
        throw new Error('No Data');
      }),
      catchError((error) => {
        throw new Error(error);
      })
    );
  }

  getPlanSummary = (planCodes: string[], vin: string, billingType?: PlanBillingType, force = false, page?: string, offeringId?: string, _cancelOrderNumbers?: string[]): Observable<PlanSummary> => {
    const rebuttalHeaders = (headers: Header[]) => {
      return this.rebuttalData$.pipe(
          take(1),
          map(rebuttalData => {
            // add header if passes into fn or in observable
            if (page || rebuttalData.page) headers.push((page && page !== rebuttalData.page) ? {page} : {page: rebuttalData.page});
            if (offeringId || rebuttalData.offeringId) headers.push((offeringId && offeringId !== rebuttalData.offeringId) ? {offeringId} : {offeringId: rebuttalData.offeringId});
            if (_cancelOrderNumbers || rebuttalData.cancelOrderNumbers) headers.push((_cancelOrderNumbers && _cancelOrderNumbers !== rebuttalData.cancelOrderNumbers) ? {cancelOrderNumbers: _cancelOrderNumbers.join(',')} : {cancelOrderNumbers: rebuttalData.cancelOrderNumbers.join(',')});
            return headers;
          })
      );
    };
    const getHeaders = () => {
      const headers = this.getBaseHeaders().concat([
        {vin},
        {planCodes: planCodes.join(',')}
      ]);
      if (billingType) headers.push({billingFrequency: billingType});
      return rebuttalHeaders(headers);
    };

    const request$ = (headers: Header[]) => this.apiCache.get<PlanSummary>({
      tokenOrUrl: apiUrls.quote.TOKEN,
      headers,
      force
    }).pipe(
        take(1),
        map((response) => {
          if (response) return response.data;
          throw new Error('No Plan Summary data');
        })
    );
    return getHeaders().pipe(switchMap((headers) => request$(headers)));
  };

  getRecurringCharges = (vin: string): Observable<RecurringCharges> => {
    const headers = this.getBaseHeaders().concat([
      {vin}
    ]);
    return this.apiCache.get({
      tokenOrUrl: apiUrls.recurringCharges.TOKEN,
      headers
    })
      .pipe(
        take(1),
        map((response) => {
          if (response) {
            return response.data as unknown as RecurringCharges;
          }
          throw new Error('No Recurring Charges');
        }),
        catchError((error) => {
          throw new Error(error);
        })
      );
  };

  getPaymentHistory = (accountNumber: string): Observable<PaymentHistory> => {
    const headers = this.getBaseHeaders().concat([
      {accountNumber: accountNumber}
    ]);
    return this.apiCache.get <PaymentHistory>(
      {
      tokenOrUrl: `${this.baseBffUrl}paymentHistory/summary/v2`,
      headers
      }
    ).pipe(
      map(({data}) => {
        if (data) return data;
        throw new Error('No Payment History');
      }),
      catchError((error) => {
        throw new Error(error);
      })
    );
  };

  getPaymentDetail = (vin:string, externalOrderNumber:string, invoiceNumber:string): Observable<PaymentHistory> => {
    const headers = this.getBaseHeaders().concat([
      {vin: vin},
      {externalOrderNumber: externalOrderNumber},
      {invoiceNumber: invoiceNumber}
    ]);

    return this.apiCache.get <PaymentHistory>(
      {
      tokenOrUrl: `${this.baseBffUrl}paymentHistory/paymentDetail/v2`,
      headers
      }
    ).pipe(
      map(({data}) => {
        if (data) return data;
        throw new Error('No Payment Detail');
      }),
      catchError((error) => {
        throw new Error(error);
      })
    );
  };

  getPaymentHistoryMethods = (accountNumber: string): Observable<PaymentHistoryPaymentMethods> => {
    const headers = this.getBaseHeaders().concat([
      {accountNumber}
    ]);

    return this.apiCache.get <PaymentHistoryPaymentMethods>(
      {
        tokenOrUrl: `${this.baseBffUrl}paymentMethods/v2`,
        headers
      }
    ).pipe(
      map(({data}) => {
        if (data) return data;
        throw new Error('No Payment Methods');
      }),
      catchError((error) => {
        throw new Error(error);
      })
    );
  };

  switchPaymentMethodPaymentHistory = (accountNumber: string, paymentMethodId: string) => {
    const headers = this.getBaseHeaders().concat([
      {accountNumber}, {paymentMethodId}
    ]);

    return this.apiCache.get<{success: boolean}>(
      {
        tokenOrUrl: `${this.baseBffUrl}paymentMethods/switch/v2`,
        headers
      }
    ).pipe(
      take(1),
      map(({data}) => {
        if (data?.success) return data;
        throw new Error('Switch payment unsuccessful');
      }),
      catchError((error) => {
        throw new Error(error);
      })
    );
  };

  deletePaymentHistoryMethod = (accountNumber: string, paymentMethodId: string) => {
    const headers = this.getBaseHeaders().concat([
      {accountNumber: accountNumber},
      {paymentMethodId: paymentMethodId}
    ]);

    return this.apiCache.delete<{success: boolean}>({
      tokenOrUrl: `${this.baseBffUrl}paymentMethods/v2`,
      headers
    }).pipe(
      map(({data}) => {
        if (data?.success) return data;
        throw new Error('Deletion failed');
      }),
        catchError((error) => {throw new Error(error);})
    );
  };

  updatePaymentHistoryMethods = (accountNumber: string, paymentMethod: PaymentHistoryPaymentMethod) => {
    const headers = this.getBaseHeaders().concat([
      {accountNumber: accountNumber}
    ]);

    const request = {
      paymentMethodId: paymentMethod.id,
      accountNumber: accountNumber,
      isDefault: paymentMethod.isDefault,
      paymentMethodType: paymentMethod.type === 'MTHD_CC' ? 'creditCard' : '',
      paymentMethod: {
        type: paymentMethod.creditCard?.type,
        name: paymentMethod.creditCard?.displayName,
        expirationMonth: paymentMethod.creditCard?.expirationMonth,
        expirationYear: paymentMethod.creditCard?.expirationYear,
        address: paymentMethod.address
      } 
    } as UpdatePaymentMethodRequest;

    return this.apiCache.put<{success: boolean}>(
      {
        tokenOrUrl: `${this.baseBffUrl}paymentMethods/v2`,
        headers,
        data: request
      }
    ).pipe(
      map(({data}) => {
        if (data?.success) return data;
        throw new Error('Update payment unsuccessful');
      }),
      catchError((error) => {
        throw new Error(error);
      })
    );
  };

  getCancelPlan = (planCodes: string[], vin: string, cancelOrderIds: string[]): Observable<CancelPlanResponse> => {
    const headers = this.getBaseHeaders().concat([
      {vin},
      {planCodes: planCodes.join(',')},
      {cancelOrderNumbers: cancelOrderIds.join(',')}
    ]);

    return this.apiCache.get<CancelPlanResponse>({
      tokenOrUrl: apiUrlMap.shop.cancelPlans,
      headers
    }).pipe(
        take(1),
        map(({data}) => {
          if (data) return data;
          throw new Error('No Cancel Plan data');
        }),
        catchError((error) => {throw new Error(error);})
      );
  };

  placeCancel = (cancelRequest: OrderRequest) => {
    const headers = this.getBaseHeaders();

    return this.apiCache.post<any>({
      tokenOrUrl: `${this.baseBffUrl}order/v2`,
      headers,
      data: cancelRequest
    }).pipe(
      take(1),
      map(({data}) => {
        if (data) return data;
        throw new Error('Place Cancel Failed');
      }),
      catchError((error) => {throw new Error(error);})
    );
  };

}
